import React, { useRef, useState, useEffect } from "react";

interface IconTabItem {
  key: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  selectedIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

interface IconTabProps {
  tabs: IconTabItem[];
  selected: string;
  onChange: (key: string) => void;
  iconSize?: number;
}

export function IconTab({ tabs, selected, onChange, iconSize = 22 }: IconTabProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  const activeIndex = tabs.findIndex((t) => t.key === selected);

  useEffect(() => {
    if (containerRef.current && activeIndex !== -1) {
      const buttons = Array.from(
        containerRef.current.querySelectorAll("button"),
      ) as HTMLElement[];
      const activeBtn = buttons[activeIndex];
      if (activeBtn) {
        setIndicatorStyle({
          left: activeBtn.offsetLeft,
          width: activeBtn.offsetWidth,
          opacity: 1,
        });
      }
    } else {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
    }
  }, [activeIndex, tabs, selected]);

  return (
    <div
      ref={containerRef}
      className="relative z-0 inline-flex items-center gap-1 bg-gray-100 p-[1px] rounded-full shadow-inner"
    >
      {/* Sliding indicator */}
      <div
        className="absolute transition-all duration-300 ease-in-out bg-white rounded-full shadow-sm"
        style={{
          left: `${indicatorStyle.left}px`,
          width: `${indicatorStyle.width}px`,
          height: "calc(100% - 2px)",
          top: "1px",
          opacity: indicatorStyle.opacity,
          zIndex: -1,
        }}
      />

      {tabs.map(({ key, icon: Icon, selectedIcon: SelectedIcon }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className="tab-pill cursor-pointer hover:bg-gray-200/50"
          style={{ backgroundColor: "transparent" }}
        >
          {selected === key ? (
            <SelectedIcon width={iconSize} height={iconSize} />
          ) : (
            <Icon width={iconSize} height={iconSize} />
          )}
        </button>
      ))}
    </div>
  );
}
